import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { api, formatMoney } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { Plus, Trash2, ListPlus, ArrowLeft, Package, Loader2, BookmarkCheck, Heart, ExternalLink, Search } from 'lucide-react';
import { SafeImage } from '@/components/marketplace/SafeImage';

function EmptyList({ name, onBack }) {
    return (
        <div className="flex flex-col items-center justify-center py-20 text-center">
            <div className="h-20 w-20 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center mb-5 shadow-inner">
                <Package className="h-9 w-9 text-red-400" />
            </div>
            <p className="text-lg font-semibold text-foreground mb-1">Lista vacía</p>
            <p className="text-sm text-muted-foreground mb-6 max-w-xs">
                Guarda productos desde cualquier anuncio usando el botón <strong>"Guardar en lista"</strong>.
            </p>
            <div className="flex gap-2">
                <Button variant="outline" size="sm" onClick={onBack}>
                    <ArrowLeft className="h-4 w-4 mr-1" />Mis listas
                </Button>
                <Link to="/search">
                    <Button size="sm" className="bg-red-600 hover:bg-red-700">
                        <Search className="h-4 w-4 mr-1" />Explorar productos
                    </Button>
                </Link>
            </div>
        </div>
    );
}

export default function ListsPage() {
    const [lists, setLists] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [newName, setNewName] = useState('');
    const [activeList, setActiveList] = useState(null);
    const [listDetail, setListDetail] = useState(null);
    const [detailLoading, setDetailLoading] = useState(false);
    const [deleteConfirm, setDeleteConfirm] = useState(null);

    const fetchLists = useCallback(() => {
        setLoading(true);
        api.get('/me/lists')
            .then(({ data }) => setLists(data))
            .catch(() => toast.error('Error al cargar listas'))
            .finally(() => setLoading(false));
    }, []);

    useEffect(() => { fetchLists(); }, [fetchLists]);

    const handleCreate = async () => {
        if (!newName.trim()) return;
        try {
            const { data } = await api.post('/me/lists', { name: newName.trim() });
            setLists(prev => [data, ...prev]);
            setNewName('');
            setCreating(false);
            toast.success('Lista creada');
        } catch {
            toast.error('No se pudo crear la lista');
        }
    };

    const handleDelete = async (listId) => {
        try {
            await api.delete(`/me/lists/${listId}`);
            setLists(prev => prev.filter(l => l.id !== listId));
            if (activeList === listId) { setActiveList(null); setListDetail(null); }
            setDeleteConfirm(null);
            toast.success('Lista eliminada');
        } catch {
            toast.error('No se pudo eliminar la lista');
        }
    };

    const openList = async (list) => {
        setActiveList(list.id);
        setDetailLoading(true);
        try {
            const { data } = await api.get(`/me/lists/${list.id}`);
            setListDetail(data);
        } catch {
            toast.error('Error al cargar la lista');
        } finally {
            setDetailLoading(false);
        }
    };

    const removeFromList = async (adId) => {
        try {
            await api.delete(`/me/lists/${activeList}/items/${adId}`);
            setListDetail(prev => ({
                ...prev,
                ads: (prev.ads || []).filter(a => a.id !== adId),
                items: (prev.items || []).filter(i => i !== adId),
            }));
            setLists(prev => prev.map(l =>
                l.id === activeList ? { ...l, items: (l.items || []).filter(i => i !== adId) } : l
            ));
            toast.success('Producto quitado de la lista');
        } catch {
            toast.error('Error al quitar el producto');
        }
    };

    // ── Detail view ──────────────────────────────────────────────────────────
    if (activeList && listDetail) {
        const list = lists.find(l => l.id === activeList);
        const ads = listDetail.ads || [];

        return (
            <div>
                {/* Header */}
                <div className="flex items-center gap-3 mb-6">
                    <button
                        onClick={() => { setActiveList(null); setListDetail(null); }}
                        className="h-9 w-9 rounded-xl border flex items-center justify-center hover:bg-secondary transition-colors"
                    >
                        <ArrowLeft className="h-4 w-4" />
                    </button>
                    <div className="flex-1 min-w-0">
                        <h2 className="text-xl font-bold truncate">{list?.name}</h2>
                        <p className="text-xs text-muted-foreground">{ads.length} {ads.length === 1 ? 'producto' : 'productos'}</p>
                    </div>
                </div>

                {detailLoading ? (
                    <div className="flex justify-center py-20">
                        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                    </div>
                ) : ads.length === 0 ? (
                    <EmptyList name={list?.name} onBack={() => { setActiveList(null); setListDetail(null); }} />
                ) : (
                    <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                        {ads.map(ad => {
                            const image = ad.media?.find(m => m.kind === 'image')?.url;
                            return (
                                <div key={ad.id} className="group rounded-2xl border bg-card overflow-hidden shadow-sm hover:shadow-md transition-shadow relative">
                                    <Link to={`/ad/${ad.id}`}>
                                        <div className="aspect-square bg-muted overflow-hidden">
                                            {image
                                                ? <SafeImage src={image} alt={ad.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                                                : <div className="w-full h-full flex items-center justify-center text-muted-foreground">
                                                    <Package className="h-8 w-8" />
                                                </div>
                                            }
                                        </div>
                                        <div className="p-2.5">
                                            <p className="text-xs font-medium line-clamp-2 leading-relaxed">{ad.title}</p>
                                            <p className="text-sm font-bold mt-1 text-foreground">{formatMoney(ad.price, ad.currency)}</p>
                                        </div>
                                    </Link>

                                    {/* Action buttons overlay */}
                                    <div className="absolute top-2 right-2 flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <Link to={`/ad/${ad.id}`}>
                                            <button className="h-7 w-7 rounded-full bg-white/95 shadow-md flex items-center justify-center hover:bg-white hover:text-red-600 transition-colors">
                                                <ExternalLink className="h-3.5 w-3.5" />
                                            </button>
                                        </Link>
                                        <button
                                            onClick={() => removeFromList(ad.id)}
                                            className="h-7 w-7 rounded-full bg-white/95 shadow-md flex items-center justify-center hover:bg-red-50 hover:text-red-600 transition-colors"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                )}
            </div>
        );
    }

    // ── Lists view ────────────────────────────────────────────────────────────
    return (
        <div>
            {/* Header */}
            <div className="flex items-center justify-between mb-6">
                <div>
                    <h2 className="text-xl font-bold">Mis listas</h2>
                    <p className="text-sm text-muted-foreground mt-0.5">Guarda productos que te interesan</p>
                </div>
                <Button
                    size="sm"
                    className="bg-red-600 hover:bg-red-700 gap-1"
                    onClick={() => { setCreating(true); setNewName(''); }}
                >
                    <Plus className="h-4 w-4" />Nueva lista
                </Button>
            </div>

            {/* Create form */}
            {creating && (
                <div className="mb-5 p-4 rounded-2xl border bg-card shadow-sm">
                    <p className="text-sm font-medium mb-2">Nombre de la lista</p>
                    <div className="flex gap-2">
                        <Input
                            placeholder="Ej: Regalos de navidad, Tecnología…"
                            value={newName}
                            onChange={e => setNewName(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && handleCreate()}
                            autoFocus
                            className="flex-1"
                        />
                        <Button onClick={handleCreate} className="bg-red-600 hover:bg-red-700 shrink-0">Crear</Button>
                        <Button variant="ghost" onClick={() => setCreating(false)} className="shrink-0">Cancelar</Button>
                    </div>
                </div>
            )}

            {/* Delete confirm */}
            {deleteConfirm && (
                <div className="mb-4 p-4 rounded-2xl border border-red-200 bg-red-50 flex items-center justify-between gap-3">
                    <p className="text-sm text-red-800">¿Eliminar la lista <strong>"{deleteConfirm.name}"</strong>?</p>
                    <div className="flex gap-2 shrink-0">
                        <Button variant="outline" size="sm" onClick={() => setDeleteConfirm(null)}>Cancelar</Button>
                        <Button size="sm" className="bg-red-600 hover:bg-red-700" onClick={() => handleDelete(deleteConfirm.id)}>Eliminar</Button>
                    </div>
                </div>
            )}

            {loading ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
                </div>
            ) : lists.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-20 text-center">
                    <div className="h-24 w-24 rounded-3xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center mb-6 shadow-inner">
                        <Heart className="h-10 w-10 text-red-400" />
                    </div>
                    <p className="text-xl font-bold mb-1">Aún no tienes listas</p>
                    <p className="text-sm text-muted-foreground max-w-xs mb-6">
                        Crea listas para organizar los productos que te gustan. Puedes guardar cualquier anuncio usando el botón <strong>"Guardar en lista"</strong>.
                    </p>
                    <div className="flex gap-2">
                        <Button className="bg-red-600 hover:bg-red-700 gap-1" onClick={() => setCreating(true)}>
                            <Plus className="h-4 w-4" />Crear mi primera lista
                        </Button>
                        <Link to="/search">
                            <Button variant="outline" className="gap-1">
                                <Search className="h-4 w-4" />Explorar
                            </Button>
                        </Link>
                    </div>
                </div>
            ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {lists.map(list => {
                        const count = list.items?.length || 0;
                        return (
                            <div
                                key={list.id}
                                className="group relative p-4 rounded-2xl border bg-card shadow-sm hover:shadow-md hover:border-red-200 transition-all cursor-pointer"
                                onClick={() => openList(list)}
                            >
                                <div className="flex items-center gap-3">
                                    <div className="h-12 w-12 rounded-xl bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center flex-shrink-0">
                                        {count === 0
                                            ? <ListPlus className="h-5 w-5 text-red-400" />
                                            : <BookmarkCheck className="h-5 w-5 text-red-600" />
                                        }
                                    </div>
                                    <div className="flex-1 min-w-0">
                                        <p className="font-semibold text-sm truncate">{list.name}</p>
                                        <p className="text-xs text-muted-foreground mt-0.5">
                                            {count === 0 ? 'Vacía' : `${count} ${count === 1 ? 'producto' : 'productos'}`}
                                        </p>
                                    </div>
                                    <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            onClick={e => { e.stopPropagation(); setDeleteConfirm(list); }}
                                            className="h-8 w-8 rounded-lg hover:bg-red-100 hover:text-red-600 text-muted-foreground flex items-center justify-center transition-colors"
                                        >
                                            <Trash2 className="h-3.5 w-3.5" />
                                        </button>
                                        <div className="h-8 w-8 rounded-lg bg-red-50 text-red-600 flex items-center justify-center">
                                            <ArrowLeft className="h-3.5 w-3.5 rotate-180" />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
